import { useMutation } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { Col, Divider, Flex, Form, Modal, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import classNames from 'classnames';
import React, { ChangeEvent, useEffect, useState } from 'react';

import { ICalculateItemSeparationPrice } from 'apis/GraphQL/Variables/purchaseInvoice';
import { PurChaseInvoiceApi } from 'apis/PurchaseInvoiceApis';
import { Split, XIcon } from 'assets/svg';
import { Button, InputForm } from 'components';
import { TPurchaseSeparationDetail } from 'interfaces/PurchaseInvoice';
import formatNumber, {
  formatCurrency,
  toastError,
} from 'utils/constants/functions';

export interface ISplitWeightModalData extends TPurchaseSeparationDetail {
  purchaseOrderId: string;
}

interface SplitWeightModalProps {
  isVisible: boolean;
  loading?: boolean;
  data: ISplitWeightModalData;
  onCancel: () => void;
  onOk: (values: any) => Promise<void>;
}

const SplitWeightModal: React.FC<SplitWeightModalProps> = ({
  isVisible,
  loading,
  data,
  onOk,
  onCancel,
}) => {
  const [form] = useForm();
  const [weightSplit, setWeightSplit] = useState<any[]>([]);

  const weightSplitDebounce = useDebounce(weightSplit, 500);

  const { mutate: calculateItemSeparationPrice } = useMutation({
    mutationFn: (params: ICalculateItemSeparationPrice) =>
      PurChaseInvoiceApi.calculateItemSeparationPrice(params),
    onSuccess: (res: any) => {
      if (res?.errors) {
        toastError(res?.errors?.[0]?.message || 'Có lỗi xảy ra');
        return;
      }

      const response = res?.data?.calculateItemSeparationPrice?.data;
      const listSplitWeightData = response?.map((item: any) => ({
        finalPrice: item?.finalPrice?.toLocaleString('vi-vn'),
        weightThread: formatNumber(item?.measureWeightThread),
      }));

      const remainingItems = response?.reduce(
        (acc: any, currentVal: any) => {
          acc.remainPrice -= currentVal.finalPrice;
          acc.remainWeight -= currentVal.measureWeightThread;

          return acc;
        },
        {
          remainPrice: data.finalPrice,
          remainWeight: data.totalGoldWeightThread,
        },
      );

      form.setFieldsValue({
        splitItems: listSplitWeightData,
        // remainWeight: remainingItems?.remainWeight,
        remainWeight: formatCurrency({
          value: remainingItems?.remainWeight,
          format: 3,
        }),
        remainPrice: remainingItems?.remainPrice?.toLocaleString('vi-vn'),
      });
    },
    onError: (errors: any) => {
      console.log(errors);
      toastError(errors?.[0]?.message || 'Có lỗi xảy ra');
    },
  });

  const handleRemoveItem = (
    name: number,
    remove: (index: number | number[]) => void,
  ) => {
    remove(name);

    const { splitItems } = form.getFieldsValue();
    const remainingItems = splitItems?.reduce(
      (acc: any, currentVal: any) => {
        acc.remainWeight -= currentVal?.weightThread || 0;
        acc.remainPrice -= Number(
          (currentVal?.finalPrice || '0')?.replace(/\./g, ''),
        );

        return acc;
      },
      {
        remainPrice: data.finalPrice,
        remainWeight: data.totalGoldWeightThread,
      },
    );

    form.setFieldsValue({
      remainWeight: remainingItems?.remainWeight,
      remainPrice: remainingItems?.remainPrice?.toLocaleString('vi-vn'),
    });
  };

  const handleCancelModal = () => {
    onCancel();
    form.resetFields();
    setWeightSplit([]);
  };

  const checkFormValidate = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleWeightChange = (
    index: number,
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    if (/[a-zA-Z\u00C0-\u017F]/.test(value.replace(/,/g, ''))) {
      return;
    }

    const formattedValue = value.replace(/[^0-9,]/g, '');
    const [integerPart, decimalPart] = formattedValue.split(',');

    const formattedIntegerPart = integerPart
      ? parseInt(integerPart, 10).toLocaleString('vi-VN')
      : '';

    const formattedDecimalPart =
      decimalPart !== undefined ? `,${decimalPart.slice(0, 3)}` : '';
    const fieldValue = formattedIntegerPart + formattedDecimalPart;

    form.setFieldValue(['splitItems', index, 'weightThread'], fieldValue);
    setWeightSplit((prev) => {
      const updatedWeightSplit = [...prev];
      updatedWeightSplit[index] = fieldValue;
      return updatedWeightSplit;
    });
  };

  const handleCheckSplitItemsFieldError = async () => {
    const { splitItems } = form.getFieldsValue();

    splitItems?.map((item: any, index: any) => {
      if (!item?.weightThread)
        form.setFields([
          {
            name: ['splitItems', index, 'weightThread'],
            errors: ['Vui lòng nhập trọng lượng'],
          },
        ]);
    });
  };

  const handleSubmitSeparationItem = async (values: any) => {
    await handleCheckSplitItemsFieldError();
    const hasFieldsError = !!form
      .getFieldsError()
      ?.filter((fieldError) => fieldError?.errors?.length)?.length;

    if (hasFieldsError) return;

    await onOk({
      ...values,
      purchaseOrderDetailId: data?.id,
      purchaseOrderId: data?.purchaseOrderId,
    });

    handleCancelModal();
  };

  const formatToNumber = (value: string) => {
    const formattedString = value?.replace(/\./g, '')?.replace(',', '.');
    return parseFloat(formattedString);
  };

  useEffect(() => {
    if (!isVisible || !Object.keys(data).length) return;

    form.setFieldsValue({
      splitItems: [{}],
      material: `${data.tproduct.id} - ${data.tproduct.name}`,
      totalPrice: data.finalPrice?.toLocaleString('vi-vn'),
      remainPrice: data.finalPrice?.toLocaleString('vi-vn'),
      // itemWeightThread: data.totalGoldWeightThread,
      // remainWeight: data.totalGoldWeightThread,
      itemWeightThread: formatCurrency({
        value: data?.totalGoldWeightThread,
        format: 3,
      }),
      remainWeight: formatCurrency({
        value: data?.totalGoldWeightThread,
        format: 3,
      }),
    });
  }, [form, data, isVisible]);

  useEffect(() => {
    if (
      !data ||
      !weightSplitDebounce.length ||
      (data && !Object.keys(data).length)
    )
      return;

    const params: ICalculateItemSeparationPrice = {
      purchaseOrderId: data.purchaseOrderId,
      purchaseOrderDetailId: data.id?.toString(),
      items: weightSplitDebounce?.map((item: any) => ({
        weightThread: formatToNumber(item),
      })),
    };
    calculateItemSeparationPrice(params);
  }, [data, form, weightSplitDebounce]);

  return (
    <Modal
      width={800}
      footer={null}
      open={isVisible}
      title="Tách trọng lượng"
      onOk={onOk}
      onCancel={handleCancelModal}
    >
      <Divider />
      <Form layout="vertical" form={form} onFinish={handleSubmitSeparationItem}>
        <Form.Item label="NVL" name="material" className="mb-4">
          <InputForm readOnly />
        </Form.Item>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <Form.Item label="TL (chỉ)" name="itemWeightThread" className="!mb-0">
            <InputForm
              readOnly
              suffix={<p className="text-[#909294]">Chỉ</p>}
            />
          </Form.Item>
          <Form.Item label="Thành tiền" name="totalPrice" className="!mb-0">
            <InputForm
              readOnly
              suffix={<p className="text-[#909294]">VND</p>}
            />
          </Form.Item>
        </div>
        <Form.Item>
          <Form.List name="splitItems">
            {(fields, { add, remove }) => (
              <>
                <Button
                  fill
                  type="button"
                  title="Tách thêm"
                  loading={loading}
                  iconBefore={<Split />}
                  customClass={classNames(
                    'border-none mb-4',
                    loading ? '' : '!bg-[#0077CC]',
                  )}
                  handleOnclick={() => add()}
                />
                {fields.map(({ key, name }) => (
                  <div
                    key={key}
                    className="grid grid-cols-2 items-center justify-center gap-4"
                  >
                    <Form.Item
                      className="mb-4"
                      label="Trọng lượng"
                      name={[name, 'weightThread']}
                      rules={[
                        {
                          pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
                          message: 'Trọng lượng chỉ được nhập số',
                        },
                      ]}
                    >
                      <InputForm
                        allowClear={false}
                        placeholder="Nhập trọng lượng"
                        suffix={<p className="text-[#909294]">Chỉ</p>}
                        onChange={(e) => handleWeightChange(name, e)}
                      />
                    </Form.Item>
                    <Flex align="center" gap={16}>
                      <Form.Item
                        label="Thành tiền"
                        className="flex-1 mb-4"
                        name={[name, 'finalPrice']}
                      >
                        <InputForm
                          readOnly
                          allowClear={false}
                          placeholder="Thành tiền sẽ tự hiển thị"
                          suffix={<p className="text-[#909294]">VND</p>}
                        />
                      </Form.Item>
                      <XIcon
                        className={classNames(
                          'border rounded-md',
                          loading
                            ? '[&>path]:fill-white bg-[#C2C2C2] cursor-not-allowed'
                            : 'cursor-pointer border-[#DC362E] hover:bg-[#f2dede]',
                        )}
                        onClick={() => handleRemoveItem(name, remove)}
                      />
                    </Flex>
                  </div>
                ))}
              </>
            )}
          </Form.List>
        </Form.Item>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item label="Trọng lượng còn lại" name="remainWeight">
            <InputForm
              readOnly
              suffix={<p className="text-[#909294]">Chỉ</p>}
            />
          </Form.Item>
          <Form.Item label="Thành tiền" name="remainPrice">
            <InputForm
              readOnly
              suffix={<p className="text-[#909294]">VND</p>}
            />
          </Form.Item>
        </div>
        <Row justify="end" align="middle" className="gap-2">
          <Col>
            <Button
              title="Hủy"
              type="button"
              loading={loading}
              handleOnclick={handleCancelModal}
            />
          </Col>
          <Col>
            <Button fill title="Tách" type="submit" loading={loading} />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default SplitWeightModal;
